// Modern Gmail Service using Google Identity Services (new approach)

interface EmailData {
  to: string;
  subject: string;
  htmlBody: string;
  replyTo?: string;
}

interface GmailSendResponse {
  id: string;
  threadId: string;
  success: boolean;
  error?: string;
}

class GmailService {
  private static instance: GmailService;
  private accessToken: string | null = null;
  private isInitialized = false;
  
  private readonly CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  private readonly SCOPES = 'https://www.googleapis.com/auth/gmail.send';

  static getInstance(): GmailService {
    if (!GmailService.instance) {
      GmailService.instance = new GmailService();
    }
    return GmailService.instance;
  }

  async initialize(): Promise<boolean> {
    try {
      if (this.isInitialized) return true;
      
      console.log('Initializing Modern Gmail API...');
      console.log('Client ID:', this.CLIENT_ID);
      
      if (!this.CLIENT_ID) {
        throw new Error('Missing Google Client ID. Please check your environment variables.');
      }

      // Load Google Identity Services
      await this.loadGoogleIdentityServices();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Modern Gmail service initialization failed:', error);
      return false;
    }
  }

  private loadGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        console.log('Google Identity Services loaded');
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services'));
      };
      document.head.appendChild(script);
    });
  }

  async authenticate(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve, reject) => {
        const google = (window as any).google;
        if (!google?.accounts) {
          reject(new Error('Google Identity Services not loaded'));
          return;
        }

        const tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: this.CLIENT_ID,
          scope: this.SCOPES,
          callback: (response: any) => {
            if (response.error) {
              console.error('OAuth error:', response.error);
              reject(new Error(response.error));
              return;
            }
            
            console.log('OAuth success, received access token');
            this.accessToken = response.access_token;
            resolve(true);
          },
        });

        tokenClient.requestAccessToken();
      });
    } catch (error) {
      console.error('Modern Gmail authentication failed:', error);
      return false;
    }
  }

  async signOut(): Promise<void> {
    this.accessToken = null;
    const google = (window as any).google;
    if (google?.accounts?.oauth2) {
      google.accounts.oauth2.revoke(this.accessToken);
    }
  }

  isSignedIn(): boolean {
    return this.accessToken !== null;
  }

  getCurrentUserEmail(): string {
    // We'll need to make an API call to get user info
    // For now, return empty string - we can enhance this later
    return '';
  }

  private createRFC2822Message(emailData: EmailData, fromEmail?: string): string {
    const { to, subject, htmlBody, replyTo } = emailData;
    
    // Use a simpler approach for Gmail API - single part HTML message
    let message = [
      fromEmail ? `From: ${fromEmail}` : '',
      `To: ${to}`,
      `Subject: ${subject}`,
      replyTo ? `Reply-To: ${replyTo}` : '',
      `MIME-Version: 1.0`,
      `Content-Type: text/html; charset=utf-8`,
      `Content-Transfer-Encoding: quoted-printable`,
      ``,
      this.encodeQuotedPrintable(htmlBody)
    ].filter(line => line !== '').join('\r\n');


    return message;
  }

  private encodeQuotedPrintable(text: string): string {
    // Simple quoted-printable encoding for HTML content
    return text
      .replace(/=/g, '=3D')
      .replace(/\r\n/g, '\r\n')
      .replace(/\n/g, '\r\n')
      .replace(/(.{70,75})/g, '$1=\r\n'); // Soft line breaks for long lines
  }

  async sendEmail(emailData: EmailData): Promise<GmailSendResponse> {
    try {
      if (!this.isSignedIn()) {
        throw new Error('Not authenticated with Gmail');
      }

      const rawMessage = this.createRFC2822Message(emailData);
      const encodedMessage = btoa(unescape(encodeURIComponent(rawMessage)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          raw: encodedMessage
        })
      });

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return {
        id: result.id,
        threadId: result.threadId,
        success: true
      };
    } catch (error: any) {
      console.error('Failed to send email:', error);
      return {
        id: '',
        threadId: '',
        success: false,
        error: error.message || 'Failed to send email'
      };
    }
  }

  async sendTestEmail(testEmailAddress: string, emailData: EmailData): Promise<GmailSendResponse> {
    const testEmailData = {
      ...emailData,
      to: testEmailAddress,
      subject: `[TEST] ${emailData.subject}`,
      htmlBody: `
        <div style="background: #fff3cd; border: 1px solid #ffeaa7; padding: 10px; margin-bottom: 20px; border-radius: 4px;">
          <strong>🧪 This is a test email</strong><br>
          Original recipient would be: <strong>${emailData.to}</strong><br>
          Test sent to: <strong>${testEmailAddress}</strong>
        </div>
        ${emailData.htmlBody}
      `
    };

    return this.sendEmail(testEmailData);
  }

  async getRemainingQuota(): Promise<number> {
    // Gmail API doesn't provide quota info directly
    return 10000; // Conservative estimate
  }
}

export default GmailService;